import {Signal, signal} from "@angular/core"
import {CloneStampPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/clone-stamp/panel/clone-stamp-panel.component"
import {CloneStampToolbox} from "@app/textures/texture-editor/operator-stack/operators/clone-stamp/toolbox/clone-stamp-toolbox"
import {deepCopy} from "@cm/utils"
import {Operator, OperatorPanelComponentType, OperatorProcessingHints} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {BrushSettings, BrushStroke} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/definition"
import {ImageOpNodeOperatorCloneStamp} from "@app/textures/texture-editor/operator-stack/operators/clone-stamp/node/node"
import {CloneStampStroke} from "@app/textures/texture-editor/operator-stack/operators/clone-stamp/node/definition"
import {
    AnyImageOpResult,
    AnyImageOpResultNode,
    ValueOrNode,
    ValueOrNodeRecursive,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeCreateImage} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/node"
import {ImageOpNodeBrushStrokes} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/node"
import {ImageOpNodeDataObject} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/node"
import {ImageOpNodeExtractRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/node"
import {BakedMaskCache} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/mask-brush-bake/baked-mask-cache"
import {Box2Like, Vector2Like} from "@cm/math"
import {merge} from "rxjs"

export class OperatorCloneStamp extends OperatorBase<TextureEditNodes.OperatorCloneStamp> {
    readonly panelComponentType: OperatorPanelComponentType = CloneStampPanelComponent
    readonly canvasToolbox: CloneStampToolbox

    readonly type = "operator-clone-stamp" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorCloneStamp | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-clone-stamp",
                enabled: true,
                strokes: [],
            },
        )
        this.canvasToolbox = new CloneStampToolbox(this)
        this._$brushSettings = signal<BrushSettings>(this.canvasToolbox.brushToolboxItem.brushSettings, {equal: () => false})
        this.$brushSettings = this._$brushSettings.asReadonly()
        merge(this.canvasToolbox.brushToolboxItem.hoverPositionChanged, this.canvasToolbox.modeChanged).subscribe(() => this.requestEval())
    }

    // OperatorBase
    // override async init() {
    //     await super.init()
    //
    //     this.currentStrokeDrawableImageHandle = await this.callback.imageOpContextWebGL2.createDrawableImageHandle({
    //         width: 1,
    //         height: 1,
    //         channelLayout: "R",
    //         dataType: "uint8norm",
    //     })
    //     this.canvasToolbox.brushToolboxItem.setDrawableImage(this.currentStrokeDrawableImageHandle.ref, "reset")
    //
    //     await Promise.all(
    //         this.node.strokes.map(async (stroke) => {
    //             if (stroke.maskReference.type !== "data-object-reference") {
    //                 throw new Error(`Unexpected mask reference type: ${JSON.stringify(stroke.maskReference)}`)
    //             }
    //             return this.callback.imageOpContextWebGL2
    //                 .createDrawableImageHandle({
    //                     dataObjectId: stroke.maskReference.dataObjectId,
    //                     channelLayout: "R",
    //                     dataType: "uint8norm",
    //                 })
    //                 .then((drawableImageHandle) => this.drawableImageHandleByMaskReference.set(stroke.maskReference, drawableImageHandle))
    //         }),
    //     )
    // }

    // OperatorBase
    override dispose(): void {
        this.bakedMaskInfoByStroke.forEach((bakedMaskInfo) => bakedMaskInfo.maskBakeCache.dispose())
        this.bakedMaskInfoByStroke.clear()
        this.sharedImageOpNodeBag.dispose()
        super.dispose()
        this.canvasToolbox.remove()
    }

    // OperatorBase
    async clone(): Promise<Operator> {
        const clonedOperator = new OperatorCloneStamp(this.callback, deepCopy(this.node))
        await clonedOperator.init()
        this.brushStrokeByCloneStampStroke.forEach((brushStroke, stroke) => {
            clonedOperator.brushStrokeByCloneStampStroke.set(clonedOperator.node.strokes[this.node.strokes.indexOf(stroke)], deepCopy(brushStroke))
        })
        return clonedOperator
    }

    private readonly _$showMask = signal(false)
    readonly $showMask = this._$showMask.asReadonly()

    setShowMask(value: boolean): void {
        this._$showMask.set(value)
        this.requestEval()
    }

    private readonly _$canUndoStroke = signal(this.node.strokes.length > 0)
    readonly $canUndoStroke = this._$canUndoStroke.asReadonly()

    undoStroke(): void {
        if (this.node.strokes.length === 0) {
            return
        }
        const lastStroke = this.node.strokes.pop()!
        this.brushStrokeByCloneStampStroke.delete(lastStroke)

        const bakedMaskInfo = this.bakedMaskInfoByStroke.get(lastStroke)
        if (bakedMaskInfo) {
            bakedMaskInfo.maskBakeCache.dispose()
            this.bakedMaskInfoByStroke.delete(lastStroke)
        }

        this._$canUndoStroke.set(this.node.strokes.length > 0)
        this.markEdited()
        this.requestEval()
    }

    addCloneStampEdit() {
        const brushStroke = deepCopy(this.canvasToolbox.brushToolboxItem.currentBrushStroke)
        if (!brushStroke) {
            return
        }
        const cloneStampStroke: TextureEditNodes.CloneStampStroke = {
            maskReference: {
                type: "data-object-reference",
                dataObjectId: "",
            },
            maskRegion: {
                type: "region",
                x: 0,
                y: 0,
                width: 0,
                height: 0,
            },
            sourceOffsetInPixels: this.canvasToolbox.sourceOffset.clone(),
        }
        this.node.strokes.push(cloneStampStroke)
        this.brushStrokeByCloneStampStroke.set(cloneStampStroke, brushStroke)
        this.canvasToolbox.brushToolboxItem.removeAllStrokes()

        this._$canUndoStroke.set(true)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$brushSettings: ReturnType<typeof signal<BrushSettings>>
    readonly $brushSettings: Signal<BrushSettings>

    setBrushSettings(value: BrushSettings): void {
        this._$brushSettings.set(value)
        this.canvasToolbox.brushToolboxItem.brushSettings = value
    }

    private sharedImageOpNodeBag = new ImageOpNodeBag("OperatorCloneStampShared")

    // OperatorBase
    protected override async createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        this.sharedImageOpNodeBag.beginFrame()
        const maskImageDescriptor = this.sharedImageOpNodeBag.getLambda("getSourceImageDescriptor", {sourceImage}, ({sourceImage}) => ({
            ...sourceImage.descriptor,
            channels: 1,
        }))
        const strokes: ValueOrNodeRecursive<CloneStampStroke>[] = []
        for (const [index, stroke] of this.node.strokes.entries()) {
            const prefix = "stroke" + index
            let strokeParameters:
                | {
                      maskImage: AnyImageOpResultNode
                      maskOffset: ValueOrNode<Vector2Like>
                      sourceOffset: ValueOrNode<Vector2Like>
                  }
                | undefined = undefined
            if (stroke.maskReference.dataObjectId) {
                strokeParameters = {
                    maskImage: this.sharedImageOpNodeBag.getNode(prefix + "LoadMask", ImageOpNodeDataObject, {
                        dataObjectId: stroke.maskReference.dataObjectId,
                        downloadFormat: "exr",
                        channels: 1,
                    }),
                    maskOffset: {
                        x: stroke.maskRegion.x,
                        y: stroke.maskRegion.y,
                    },
                    sourceOffset: {
                        x: stroke.sourceOffsetInPixels.x,
                        y: stroke.sourceOffsetInPixels.y,
                    },
                }
            } else {
                const brushStroke = this.brushStrokeByCloneStampStroke.get(stroke)
                if (brushStroke) {
                    let bakedMaskInfo = this.bakedMaskInfoByStroke.get(stroke)
                    if (!bakedMaskInfo) {
                        let maskImage: AnyImageOpResultNode = this.sharedImageOpNodeBag.getNode(prefix + "CreateEmptyMaskImage", ImageOpNodeCreateImage, {
                            descriptor: maskImageDescriptor,
                            data: 0,
                        })
                        const nodeBrushStrokes = this.sharedImageOpNodeBag.getNode(prefix + "BrushStrokes", ImageOpNodeBrushStrokes, {
                            sourceImage: maskImage,
                            brushStrokes: [brushStroke],
                            options: {
                                tilingEnabled: true,
                            },
                        })
                        maskImage = this.sharedImageOpNodeBag.getProperty(prefix + "GetBrushStrokeResultImage", nodeBrushStrokes, "resultImage")
                        const boundingBox = this.sharedImageOpNodeBag.getProperty(prefix + "GetBrushStrokeBoundingBox", nodeBrushStrokes, "boundingBox")
                        const originalSourceOffset = {
                            ...stroke.sourceOffsetInPixels,
                        }
                        bakedMaskInfo = {
                            maskImage,
                            boundingBox,
                            originalSourceOffset,
                            maskBakeCache: new BakedMaskCache(this.callback.texturesApi, this.callback.organization.legacyId),
                        }
                        this.bakedMaskInfoByStroke.set(stroke, bakedMaskInfo)
                    }
                    if (hints.mode === "final") {
                        const {dataObjectId, region} = await bakedMaskInfo.maskBakeCache.getDataObjectId() // bake/upload happens here, once
                        stroke.maskReference.dataObjectId = dataObjectId
                        stroke.maskRegion = {type: "region", ...region}
                        stroke.sourceOffsetInPixels.x += region.x
                        stroke.sourceOffsetInPixels.y += region.y
                        this.brushStrokeByCloneStampStroke.delete(stroke)
                        strokeParameters = {
                            maskImage: this.sharedImageOpNodeBag.getNode(prefix + "LoadBakedMask", ImageOpNodeDataObject, {
                                dataObjectId,
                                downloadFormat: "exr",
                                channels: 1,
                            }),
                            maskOffset: {
                                x: region.x,
                                y: region.y,
                            },
                            sourceOffset: {
                                x: stroke.sourceOffsetInPixels.x,
                                y: stroke.sourceOffsetInPixels.y,
                            },
                        }
                    } else {
                        strokeParameters = {
                            maskImage: bakedMaskInfo.maskBakeCache.captureViaGraph(
                                imageOpNodeBag,
                                prefix + "CaptureMask",
                                bakedMaskInfo.maskImage,
                                bakedMaskInfo.boundingBox,
                            ),
                            maskOffset: {x: 0, y: 0},
                            sourceOffset: bakedMaskInfo.originalSourceOffset,
                        }
                    }
                }
            }
            if (strokeParameters) {
                strokes.push(strokeParameters)
            }
        }
        if (hints.mode === "preview") {
            // draw current stroke / preview stamp
            const lastHoverPosition = this.canvasToolbox.brushToolboxItem.lastHoverPosition?.round()
            const setPreviewStamp = this.canvasToolbox.mode === "brush" && !this.canvasToolbox.brushToolboxItem.isDrawing && lastHoverPosition
            if (setPreviewStamp) {
                // we set a dummy brush stroke here to draw a stamp preview
                this.canvasToolbox.brushToolboxItem.removeAllStrokes()
                this.canvasToolbox.brushToolboxItem.addStroke({
                    settings: this.canvasToolbox.brushToolboxItem.brushSettings,
                    path: [lastHoverPosition],
                })
                this.canvasToolbox.brushToolboxItem.brushStartPosition = lastHoverPosition
            }
            const maskImage: ValueOrNode<AnyImageOpResult> = this.sharedImageOpNodeBag.getNode("currentStrokeMask", ImageOpNodeCreateImage, {
                descriptor: maskImageDescriptor,
                data: 0,
            })
            const brushToolNodes = this.canvasToolbox.brushToolboxItem.getImageOpNodes({
                sourceImage: maskImage,
            })
            const croppedMaskImage = this.sharedImageOpNodeBag.getNode("croppedCurrentStrokeMask", ImageOpNodeExtractRegion, {
                sourceImage: brushToolNodes.resultImage,
                region: brushToolNodes.boundingBox,
            })
            const maskOffset = this.sharedImageOpNodeBag.getLambda(
                "getCurrentStrokeMaskOffset",
                {boundingBox: brushToolNodes.boundingBox},
                ({boundingBox}) => ({
                    x: boundingBox.x,
                    y: boundingBox.y,
                }),
            )
            const sourceOffset = this.sharedImageOpNodeBag.getLambda(
                "getCurrentStrokeSourceOffset",
                {boundingBox: brushToolNodes.boundingBox},
                ({boundingBox}) => ({
                    x: this.canvasToolbox.sourceOffset.x + boundingBox.x,
                    y: this.canvasToolbox.sourceOffset.y + boundingBox.y,
                }),
            )
            if (setPreviewStamp || this.canvasToolbox.brushToolboxItem.isDrawing) {
                strokes.push({
                    maskImage: croppedMaskImage,
                    maskOffset,
                    sourceOffset,
                })
            }
        }
        this.sharedImageOpNodeBag.endFrame()
        return imageOpNodeBag.getNode("cloneStamp", ImageOpNodeOperatorCloneStamp, {
            sourceImage,
            drawMaskOnly: hints.mode === "preview" && this.$showMask() && this.selected,
            strokes,
        })
    }

    private brushStrokeByCloneStampStroke = new Map<TextureEditNodes.CloneStampStroke, BrushStroke>()
    private bakedMaskInfoByStroke = new Map<TextureEditNodes.CloneStampStroke, BakedMaskInfo>()
}

type BakedMaskInfo = {
    maskImage: AnyImageOpResultNode
    boundingBox: ValueOrNode<Box2Like>
    originalSourceOffset: Vector2Like
    maskBakeCache: BakedMaskCache
}
